import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { BrandLogo } from './BrandLogo';
import { AccountMenu, UserAvatar } from './AccountMenu';

function useAlphaFree() {
  const { balance, productConfig } = useAuth();
  return Boolean(
    productConfig?.restorationsCurrentlyFree ||
      balance?.restorationsCurrentlyFree ||
      productConfig?.creditMode === 'disabled' ||
      productConfig?.creditMode === 'metered' ||
      productConfig?.stage === 'alpha' ||
      !productConfig
  );
}

function AccessStatus() {
  const { balance, productConfig } = useAuth();
  const free = useAlphaFree();
  const stage = productConfig?.stage || balance?.stage || 'alpha';

  if (free) {
    const fullLabel = stage === 'beta' ? 'Kinvera Beta' : 'Kinvera Alpha';
    const shortLabel = stage === 'beta' ? 'Beta' : 'Alpha';
    return (
      <span className="kin-credits kin-access-badge" title="Restorations are free during testing">
        <span className="kin-access-badge-full">
          {fullLabel}
          <span className="kin-access-badge-sub"> · Restorations currently free</span>
        </span>
        <span className="kin-access-badge-short" aria-hidden="true">
          {shortLabel}
        </span>
      </span>
    );
  }

  return (
    <span className="kin-credits" title="Restoration credits">
      {balance
        ? `${balance.credits} credit${balance.credits === 1 ? '' : 's'}${
            balance.freeRestorationsRemaining
              ? ` · ${balance.freeRestorationsRemaining} free`
              : ''
          }`
        : '…'}
    </span>
  );
}

export const SiteHeader: React.FC = () => {
  const { user, isDevAuth, logout, productConfig, balance } = useAuth();
  const alphaFree = useAlphaFree();
  const billingEnabled = Boolean(productConfig?.billingEnabled);
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const headerRef = useRef<HTMLElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);

  const stage = productConfig?.stage || balance?.stage || 'alpha';
  const stageName = stage === 'beta' ? 'Kinvera Beta' : 'Kinvera Alpha';

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMenu();
        toggleRef.current?.focus();
      }
    };
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (headerRef.current && target && !headerRef.current.contains(target)) {
        closeMenu();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [menuOpen, closeMenu]);

  return (
    <header className={`kin-header${menuOpen ? ' kin-header-menu-open' : ''}`} ref={headerRef}>
      <div className="kin-header-bar">
        <BrandLogo size="lg" className="kin-header-logo" />

        <nav className="kin-nav" aria-label="Primary">
          <NavLink to="/how-it-works">How it works</NavLink>
          {(billingEnabled || !alphaFree) && <NavLink to="/pricing">Pricing</NavLink>}
          <NavLink to="/app">Restore</NavLink>
          {user && <NavLink to="/app/upscale">Prepare for Print</NavLink>}
          {user && <NavLink to="/app/library">Library</NavLink>}
        </nav>

        <div className="kin-header-actions">
          {(user || alphaFree) && <AccessStatus />}
          {user ? (
            <div className="kin-header-desktop-only">
              <AccountMenu />
            </div>
          ) : (
            <Link to="/login" className="kin-btn kin-btn-primary kin-header-desktop-only">
              Sign in
            </Link>
          )}

          <button
            ref={toggleRef}
            type="button"
            className="kin-menu-toggle"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X className="kin-menu-toggle-icon" aria-hidden /> : <Menu className="kin-menu-toggle-icon" aria-hidden />}
          </button>
        </div>
      </div>

      <div
        id={menuId}
        className={`kin-mobile-menu${menuOpen ? ' is-open' : ''}`}
        aria-hidden={!menuOpen}
      >
        {user && (
          <div className="kin-mobile-menu-user">
            <UserAvatar size={40} />
            <div>
              <strong>{user.displayName || user.email || 'Account'}</strong>
              {user.email ? <span>{user.email}</span> : null}
            </div>
          </div>
        )}

        <nav className="kin-mobile-menu-nav" aria-label="Mobile">
          <NavLink to="/how-it-works" onClick={closeMenu}>
            How it works
          </NavLink>
          {(billingEnabled || !alphaFree) && (
            <NavLink to="/pricing" onClick={closeMenu}>
              Pricing
            </NavLink>
          )}
          <NavLink to="/app" onClick={closeMenu}>
            Restore
          </NavLink>
          {user && (
            <>
              <NavLink to="/app/upscale" onClick={closeMenu}>
                Prepare for Print
              </NavLink>
              <NavLink to="/app/library" onClick={closeMenu}>
                Library
              </NavLink>
              <NavLink to="/app/account" onClick={closeMenu}>
                Account
              </NavLink>
              <NavLink to="/app/account#billing" onClick={closeMenu}>
                Billing &amp; credits
              </NavLink>
              <NavLink to="/app/account#connected" onClick={closeMenu}>
                Google Photos
              </NavLink>
            </>
          )}
        </nav>

        {alphaFree && (
          <div className="kin-mobile-menu-status">
            <strong>{stageName}</strong>
            <p>Restorations currently free while we test Kinvera.</p>
          </div>
        )}

        {!alphaFree && user && balance && (
          <div className="kin-mobile-menu-status">
            <strong>
              {balance.credits} credit{balance.credits === 1 ? '' : 's'}
            </strong>
            {balance.freeRestorationsRemaining ? (
              <p>{balance.freeRestorationsRemaining} free restorations remaining</p>
            ) : null}
          </div>
        )}

        <div className="kin-mobile-menu-account">
          {user ? (
            !isDevAuth && (
              <button
                type="button"
                className="kin-btn kin-btn-ghost kin-mobile-menu-account-btn"
                onClick={() => {
                  closeMenu();
                  logout();
                }}
              >
                Sign out
              </button>
            )
          ) : (
            <Link to="/login" className="kin-btn kin-btn-primary kin-mobile-menu-account-btn" onClick={closeMenu}>
              Sign in
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export const MarketingLayout: React.FC = () => {
  const alphaFree = useAlphaFree();
  const { productConfig } = useAuth();
  const billingEnabled = Boolean(productConfig?.billingEnabled);
  return (
    <div className="kin-shell">
      <SiteHeader />
      <Outlet />
      <footer className="kin-footer">
        <div className="kin-footer-brand">
          <BrandLogo size="md" to="/" />
          <p>Restore the photo. Preserve the person.</p>
        </div>
        <div className="kin-footer-links">
          {(billingEnabled || !alphaFree) && <Link to="/pricing">Pricing</Link>}
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </div>
      </footer>
    </div>
  );
};

export const AppLayout: React.FC = () => {
  const location = useLocation();
  const isStudioHome = location.pathname === '/app' || location.pathname.startsWith('/app/integrations');
  return (
    <div className={`kin-shell kin-shell-app${isStudioHome ? ' kin-shell-studio' : ''}`}>
      {!isStudioHome && <SiteHeader />}
      <Outlet />
    </div>
  );
};
